import React, { FC, ReactNode, createContext, useContext, useEffect, useState } from 'react';

import {
  IImageFragment,
  ILocaleTypographyBlock,
  useFeatureOnboardingQuery,
} from 'generated/sanity-graphql';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { maybeLocaleImage } from 'utils/graphql';
import { getLocaleTextBlock } from 'utils/sanity';

export interface IOnboardingContext {
  onboardingEnabled: boolean;
  allFeatureOnboarding: IFeatureOnboarding[];
  featureOnboardingLoading: boolean;
}

export interface IOnboardingProps {
  children: ReactNode;
}

type IFeatureOnboardingScreen =
  | {
      image: IImageFragment | null;
      textContent: ILocaleTypographyBlock;
    }
  | null
  | undefined;

export interface IFeatureOnboarding {
  featureOnboardingChannel: string | null;
  featureOnboardingTriggerRule: string | null;
  featureOnboardingScreens: IFeatureOnboardingScreen[] | undefined;
}

export const OnboardingContext = createContext<IOnboardingContext>({} as IOnboardingContext);
export const useOnboardingContext = () => useContext<IOnboardingContext>(OnboardingContext);

export const OnboardingProvider: FC = ({ children }: IOnboardingProps) => {
  const [allFeatureOnboarding, setAllFeatureOnboarding] = useState<IFeatureOnboarding[]>([]);

  const onboardingEnabled = useFlag(LaunchDarklyFlag.ENABLE_FEATURE_ONBOARDING);
  const { data, loading } = useFeatureOnboardingQuery({
    skip: !onboardingEnabled,
  });

  useEffect(() => {
    if (data?.allFeatureOnboardings.length) {
      const featureOnboardings = data.allFeatureOnboardings.map(onboarding => {
        const featureOnboardingChannel = onboarding.channel ?? null;
        const featureOnboardingTriggerRule = onboarding.triggerRule ?? null;
        const screens = onboarding.screens;
        const featureOnboardingScreens = screens?.map(screen => {
          switch (screen?.__typename) {
            case 'ImageWithHeaderAndText':
              return {
                image: maybeLocaleImage(screen.image),
                textContent: getLocaleTextBlock(screen, 'textContent'),
              };
            default:
              return null;
          }
        });
        return {
          featureOnboardingChannel,
          featureOnboardingTriggerRule,
          featureOnboardingScreens,
        };
      });
      setAllFeatureOnboarding(featureOnboardings);
    }
  }, [data]);

  return (
    <OnboardingContext.Provider
      value={{ onboardingEnabled, allFeatureOnboarding, featureOnboardingLoading: loading }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};
